// OTE (MOM Overtime Exemption) — the AUDITOR's worksheet (/jobs/:id/ote). Four steps, each with "Next →" greyed out
// with the reason, as Internal Audit:
//   1 Start            start / end time, opening-meeting attendance, session photo (+ consent) or the reason there is none
//   2 Activities & RA   upload the RA / Risk Register (+ SWPs, other files); correct, add or remove activity rows; for
//                      each: RA reference, final risk level (Low / Not low), MOM Annex A (No / Yes)
//   3 Checklist         the 9 items (R1); Not Satisfactory → "still needed from the client" (auditor closes, with comment)
//   4 Finish            effective date of the RA, observations → Sign & finish (or Sign as Not endorsed) → send
// Saving: every change goes into the Wi-Fi-safe save queue first (kept on this device until saved, as Internal Audit).
// Every important step (start, documents, items, signing, sending) goes through the ote-actions server function.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'
import { createSaveQueue } from '../lib/iaSaveQueue'
import { onTabReturn } from '../lib/onTabReturn'
import { oteAction } from '../lib/ote'
import { OTE_ITEMS, OTE_SECTIONS, itemResult } from '../lib/oteChecklist'
import { OTE_ANNEX_A } from '../lib/oteQuote'
import { stageOf, fmtDay, otePic } from '../lib/oteJob'
import { sgToday, isOTE } from '../lib/service'
import TakePhotoButton from '../components/TakePhotoButton'

const S = 'Satisfactory', NS = 'Not Satisfactory'
const OPEN = ['scheduled', 'confirmed', 'in_progress', 'awaiting_closeout']
const REMOVE_REASONS = ['MOM Annex A', 'Client withdrew']
const sgTime = iso => { try { return iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: false }) : '' } catch (e) { return '' } }
const atTime = (date, hhmm) => (date && hhmm ? new Date(`${String(date).slice(0, 10)}T${hhmm}:00+08:00`).toISOString() : null)
const box = { width: '100%', boxSizing: 'border-box' }

async function shrinkPhoto(file) {
  try {
    const url = URL.createObjectURL(file)
    const img = await new Promise((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = bad; i.src = url })
    URL.revokeObjectURL(url)
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file
    const c = document.createElement('canvas')
    c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale)
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height)
    const blob = await new Promise(ok => c.toBlob(ok, 'image/jpeg', 0.85))
    return blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file
  } catch (e) { return file }
}

// Two or three big buttons in a row (Low / Not low, Satisfactory / Not Satisfactory …).
function Choice({ value, options, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(([v, label, tone]) => {
        const on = value === v
        const bg = !on ? 'var(--bg)' : tone === 'bad' ? '#FDECEA' : tone === 'na' ? 'var(--bg2)' : '#eaf3de'
        const fg = !on ? 'var(--text2)' : tone === 'bad' ? '#791F1F' : tone === 'na' ? 'var(--text)' : '#27500a'
        return (
          <button key={String(v)} type="button" disabled={disabled} onClick={() => onChange(v)}
            style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: on ? 600 : 400, cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${on ? fg : 'var(--border2)'}`, background: bg, color: fg }}>{label}</button>
        )
      })}
    </div>
  )
}

export default function OTEWorksheet() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, user } = useAuth()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [ws, setWs] = useState({})                 // ote_worksheet (data merged in as ws.data)
  const [acts, setActs] = useState([])
  const [answers, setAnswers] = useState({})       // item code → row
  const [docs, setDocs] = useState([])             // ote_documents + file name
  const [issues, setIssues] = useState([])
  const [parts, setParts] = useState([])           // opening-meeting attendance
  const [photos, setPhotos] = useState([])
  const [reports, setReports] = useState([])
  const [hasSig, setHasSig] = useState(null)
  const [assigned, setAssigned] = useState(false)
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState('')
  const [saveState, setSaveState] = useState('saved')
  const [pending, setPending] = useState(0)
  const [missing, setMissing] = useState([])       // server's "can't sign yet" list
  const [raReason, setRaReason] = useState('')
  const [sendTo, setSendTo] = useState('')
  const [showAnnex, setShowAnnex] = useState(false)
  const [removing, setRemoving] = useState(null)       // activity id whose "Remove" choice is open
  const [otherLabel, setOtherLabel] = useState('')     // name of the "other evidence" file to upload
  const [closeText, setCloseText] = useState({})       // item code → closing comment being typed
  const [raDate, setRaDate] = useState({})             // RA document id → effective date being entered
  const queueRef = useRef(null)
  const flushTimer = useRef(null)

  useEffect(() => { if (profile?.id) load() }, [id, profile?.id])
  useEffect(() => {
    if (loading || !job) return undefined
    const retry = setInterval(() => { if (queueRef.current?.size()) flushQueue() }, 15000)
    const back = () => flushQueue()
    const offTab = onTabReturn(back)
    window.addEventListener('online', back)
    return () => { clearInterval(retry); offTab(); window.removeEventListener('online', back) }
  }, [loading, !!job, id])

  // After signing: keep the page up to date by itself while AuditPM signs, sends or prepares the documents.
  const waitingOnServer = !!job && (['signing', 'sending'].includes(job.ote_stage) || reports.some(r => r.status === 'preparing'))
  useEffect(() => {
    if (!waitingOnServer) return undefined
    const t = setInterval(() => { if (document.visibilityState === 'visible') load() }, 8000)
    return () => clearInterval(t)
  }, [waitingOnServer, id])

  function makeQueue() {
    return createSaveQueue({
      storageKey: `ote-q:${id}:${profile?.id || 'me'}`,
      // Worksheet fields: columns as they are; "data.xxx" keys merged into ote_worksheet.data.
      saveRep: async patch => {
        const cols = {}, data = {}
        Object.entries(patch).forEach(([k, v]) => { if (k.startsWith('data.')) data[k.slice(5)] = v; else cols[k] = v })
        let merged
        if (Object.keys(data).length) {
          const { data: cur, error: rErr } = await supabase.from('ote_worksheet').select('data').eq('job_id', id).maybeSingle()
          if (rErr) throw rErr
          merged = { ...(cur?.data || {}), ...data }
        }
        const { error } = await supabase.from('ote_worksheet').upsert({ job_id: id, ...cols, ...(merged ? { data: merged } : {}), updated_by: whoName() }, { onConflict: 'job_id' })
        if (error) throw error
      },
      // "act:<id>" → an activity row; "part:<id>" → an attendee; anything else → a checklist answer (item code).
      saveResp: async (key, cols) => {
        if (key.startsWith('part:')) {
          const { error } = await supabase.from('audit_participants').update(cols).eq('id', key.slice(5))
          if (error) throw error
        } else if (key.startsWith('act:')) {
          const { data, error } = await supabase.from('ote_activities').update({ ...cols, updated_by: whoName() }).eq('id', key.slice(4)).select('id')
          if (error) throw error
          // No row changed = refused (e.g. the report was signed meanwhile) — never count it as saved.
          if (!data?.length) throw Object.assign(new Error('This activity can no longer be changed (the report may be signed). Please reload.'), { code: '23000' })
        } else {
          const { error } = await supabase.from('ote_answers').upsert({ job_id: id, item_code: key, ...cols, updated_by: whoName() }, { onConflict: 'job_id,item_code' })
          if (error) throw error
        }
      },
      onChange: n => setPending(n),
      onRejected: err => { failed('Not saved', err?.message || 'Please reload the page'); load() },
    })
  }
  const whoName = () => profile?.name || user?.email || 'Auditor'
  async function flushQueue() {
    const q = queueRef.current
    if (!q || !q.size()) { setSaveState('saved'); return true }
    try { const { data } = await supabase.auth.getSession(); if (!data?.session) { setSaveState('pending'); return false } } catch (e) { setSaveState('pending'); return false }
    setSaveState('saving')
    const ok = await q.flush()
    setSaveState(q.size() ? 'pending' : 'saved')
    return ok && !q.size()
  }
  function scheduleFlush() { setSaveState('saving'); clearTimeout(flushTimer.current); flushTimer.current = setTimeout(flushQueue, 600) }
  async function saveNow() {
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur()
    await new Promise(r => setTimeout(r, 50))
    clearTimeout(flushTimer.current)
    return flushQueue()
  }

  async function load() {
    const { data: j } = await supabase.from('jobs').select('*, clients(*)').eq('id', id).maybeSingle()
    if (!j) { setLoading(false); return }
    if (!isOTE(j.audit_type)) { navigate(`/jobs/${id}`, { replace: true }); return }   // not an OTE job → its own page
    const { data: w0 } = await supabase.from('ote_worksheet').select('*').eq('job_id', id).maybeSingle()
    if (!w0 && j.status !== 'Cancelled') { try { await oteAction({ action: 'init', jobId: id }) } catch (e) { /* shown below */ } }
    const [{ data: w }, { data: rows }, { data: ans }, { data: ds }, { data: iss }, { data: ps }, { data: ph }, { data: reps }, { data: auds }] = await Promise.all([
      supabase.from('ote_worksheet').select('*').eq('job_id', id).maybeSingle(),
      supabase.from('ote_activities').select('*').eq('job_id', id).order('sort_order').order('created_at'),
      supabase.from('ote_answers').select('*').eq('job_id', id),
      supabase.from('ote_documents').select('*').eq('job_id', id).order('created_at'),
      supabase.from('ote_issues').select('*').eq('job_id', id).order('opened_at'),
      supabase.from('audit_participants').select('*').eq('job_id', id).is('archived_at', null).order('sort_order'),
      supabase.from('audit_evidence_files').select('*').eq('job_id', id).is('archived_at', null).eq('evidence_type', 'session_photo').order('created_at'),
      supabase.from('ote_reports').select('id, kind, version, status, conclusion, created_at').eq('job_id', id).order('created_at', { ascending: false }),
      supabase.from('auditors').select('id, name, email, user_id'),
    ])
    const fileIds = (ds || []).map(d => d.evidence_file_id)
    const { data: files } = fileIds.length ? await supabase.from('audit_evidence_files').select('id, file_name, storage_path').in('id', fileIds) : { data: [] }
    const fileOf = Object.fromEntries((files || []).map(f => [f.id, f]))
    const aud = (auds || []).find(a => a.id === j.auditor_id)
    const mail = String(user?.email || '').trim().toLowerCase()
    setAssigned(!!aud && (aud.user_id === profile?.id || (!!mail && String(aud.email || '').trim().toLowerCase() === mail)))
    if (aud) {
      const { data: sg } = await supabase.from('auditor_signatures').select('id').eq('auditor_id', aud.id).is('revoked_at', null).limit(1)
      setHasSig(!!sg?.length)
    }
    const qKey = `${id}:${profile?.id}`
    if (!queueRef.current || queueRef.current.key !== qKey) queueRef.current = Object.assign(makeQueue(), { key: qKey })
    const q = queueRef.current
    // This device's unsaved changes stay on top of what is saved.
    const wsRow = q.overlayRep(w || {})
    const data = { ...(w?.data || {}) }
    Object.keys(wsRow).forEach(k => { if (k.startsWith('data.')) { data[k.slice(5)] = wsRow[k]; delete wsRow[k] } })
    setWs({ ...wsRow, data })
    const byCode = Object.fromEntries((ans || []).map(a => [a.item_code, a]))
    const over = q.overlayResp({ ...byCode, ...Object.fromEntries((rows || []).map(r => ['act:' + r.id, r])), ...Object.fromEntries((ps || []).map(p => ['part:' + p.id, p])) })
    setAnswers(Object.fromEntries(Object.entries(over).filter(([k]) => !k.startsWith('act:') && !k.startsWith('part:'))))
    setActs((rows || []).map(r => over['act:' + r.id] || r))
    setDocs((ds || []).map(d => ({ ...d, file: fileOf[d.evidence_file_id] || null })))
    setIssues(iss || []); setParts((ps || []).map(p => over['part:' + p.id] || p)); setPhotos(ph || []); setReports(reps || [])
    setJob(j)
    setSendTo(otePic(j).email)
    const s = stageOf(j)
    // Signed / closed: nothing can be saved any more — drop what this device still had queued (it would retry for ever).
    if (!OPEN.includes(s) || j.status === 'Cancelled') { q.clear(); setPending(0); setSaveState('saved') }
    else if (q.size()) flushQueue()
    setStep(OPEN.includes(s) ? (s === 'scheduled' || s === 'confirmed' ? 1 : step) : 4)
    setLoading(false)
  }

  const rows = useMemo(() => acts.filter(r => !r.removed_at), [acts])
  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (!job) return <div>Job not found.</div>

  const client = job.clients || {}
  const stage = stageOf(job)
  const started = stage === 'in_progress' || stage === 'awaiting_closeout'
  const editable = assigned && job.status !== 'Cancelled' && started
  const removed = acts.filter(r => r.removed_at)
  const ras = docs.filter(d => d.kind === 'ra').sort((a, b) => (b.version_no || 0) - (a.version_no || 0))
  const latestRa = ras[0] || null
  const acceptedRa = ras.find(d => d.accepted) || null
  const swps = docs.filter(d => d.kind === 'swp')
  const others = docs.filter(d => d.kind === 'other')
  const openIssues = issues.filter(i => i.status === 'open')
  const d = ws.data || {}
  // A row is "checked" once the auditor has gone through it with the client: RA reference, risk level and Annex A.
  const isIncomplete = r => !String(r.ra_reference || '').trim() || !r.risk_level || r.annex_a == null
  const incompleteRows = rows.map((r, i) => isIncomplete(r) ? i : -1).filter(i => i >= 0)

  // ── Saving helpers ──
  function setW(field, value) {
    const q = queueRef.current; if (!q) return
    q.put('rep', null, field, value)
    if (field.startsWith('data.')) setWs(w => ({ ...w, data: { ...(w.data || {}), [field.slice(5)]: value } }))
    else setWs(w => ({ ...w, [field]: value }))
    scheduleFlush()
  }
  function setAct(rowId, patch) {
    const q = queueRef.current; if (!q) return
    Object.entries(patch).forEach(([k, v]) => q.put('resp', 'act:' + rowId, k, v))
    setActs(list => list.map(r => r.id === rowId ? { ...r, ...patch } : r))
    scheduleFlush()
  }
  function setAns(code, patch) {
    const q = queueRef.current; if (!q) return
    const full = { ...patch, recheck: false }                     // answering again = re-confirmed
    Object.entries(full).forEach(([k, v]) => q.put('resp', code, k, v))
    setAnswers(a => ({ ...a, [code]: { ...(a[code] || {}), item_code: code, ...full } }))
    scheduleFlush()
  }
  const act = async (key, body, okMsg) => {
    setBusy(key)
    try { const r = await oteAction({ jobId: id, ...body }); if (okMsg) toast(okMsg, 'success'); await load(); setBusy(''); return r || { ok: true } }
    catch (e) { failed("That didn't work", e.message); setBusy(''); return null }
  }

  // ── Step 1: start, attendance, photo ──
  async function addAttendee() {
    const { data, error } = await supabase.from('audit_participants').insert({ job_id: id, name: '', designation: '', meeting: 'opening', sort_order: parts.length }).select().single()
    if (error) return failed("Couldn't add the row", error.message)
    setParts(p => [...p, data])
  }
  // Attendee names go through the Wi-Fi-safe queue (kept on this device until saved).
  function updAttendee(pid, patch) {
    const q = queueRef.current; if (!q) return
    Object.entries(patch).forEach(([k, v]) => q.put('resp', 'part:' + pid, k, v))
    setParts(p => p.map(x => x.id === pid ? { ...x, ...patch } : x))
    scheduleFlush()
  }
  async function delAttendee(pid) {
    const { error } = await supabase.from('audit_participants').update({ archived_at: new Date().toISOString() }).eq('id', pid)
    if (error) return failed("Couldn't remove", error.message)
    setParts(p => p.filter(x => x.id !== pid))
  }
  async function uploadPhoto(picked) {
    if (!picked || busy) return
    if (!/^image\//.test(picked.type) && !/\.(jpe?g|png|heic|heif|webp)$/i.test(picked.name)) return toast('Choose a photo', 'warning')
    setBusy('photo')
    const file = await shrinkPhoto(picked)
    const path = `${id}/ote_photo_${Date.now()}_${file.name.replace(/[^\w.-]/g, '_')}`
    const up = await supabase.storage.from('audit-evidence').upload(path, file)
    if (up.error) { setBusy(''); return failed('Upload failed', up.error.message) }
    const { data, error } = await supabase.from('audit_evidence_files').insert({ job_id: id, storage_path: path, file_name: file.name, mime: file.type, size_bytes: file.size, evidence_type: 'session_photo', title: file.name, is_annex: false }).select().single()
    setBusy('')
    if (error) return failed("Couldn't save", error.message)
    setPhotos(p => [...p, data])
  }
  async function removePhoto(pid) {
    if (!window.confirm('Remove this photo?')) return
    const { error } = await supabase.from('audit_evidence_files').update({ archived_at: new Date().toISOString() }).eq('id', pid)
    if (error) return failed("Couldn't remove", error.message)
    setPhotos(p => p.filter(x => x.id !== pid))
  }
  async function openFile(f) {
    const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(f.storage_path, 600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener'); else toast("Couldn't open the file", 'error')
  }

  // ── Step 2: documents and activities ──
  async function uploadDoc(file, kind) {
    if (!file || busy) return
    if (file.size > 50 * 1024 * 1024) return toast('The file is larger than 50 MB', 'warning')
    const label = kind === 'other' ? otherLabel.trim() : null
    const effectiveDate = null   // entered afterwards with the date picker on the RA row
    // Save first: a revised RA marks A1–B5 "re-check" on the server, which older queued answers must not undo.
    if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then try again.' })
    setBusy('doc')
    const path = `${id}/ote_${kind}_${Date.now()}_${file.name.replace(/[^\w.-]/g, '_')}`
    const up = await supabase.storage.from('audit-evidence').upload(path, file)
    if (up.error) { setBusy(''); return failed('Upload failed', up.error.message) }
    const { data: ev, error } = await supabase.from('audit_evidence_files').insert({ job_id: id, storage_path: path, file_name: file.name, mime: file.type || 'application/octet-stream', size_bytes: file.size, evidence_type: 'ote_document', title: label || file.name, is_annex: false }).select('id').single()
    setBusy('')
    if (error) return failed("Couldn't save the file", error.message)
    const ok = await act('doc', { action: 'add-document', evidenceFileId: ev.id, kind, label, effectiveDate },
      kind === 'ra' ? (latestRa ? 'Revised RA added — enter its effective date, then re-confirm A1 to B5' : 'RA added — now enter its effective date') : 'Document added')
    if (ok && kind === 'other') setOtherLabel('')
    // Not registered → put the stray file record away, so a retry doesn't leave duplicates.
    if (!ok) await supabase.from('audit_evidence_files').update({ archived_at: new Date().toISOString() }).eq('id', ev.id)
  }
  async function addActivity() {
    const { error } = await supabase.from('ote_activities').insert({ job_id: id, sort_order: acts.length, source: 'auditor', work_activities: '', updated_by: whoName() })
    if (error) return failed("Couldn't add the row", error.message)
    if (!(await saveNow())) toast('Some changes are still waiting to save', 'warning')
    await load()
  }
  // "Split this row": a copy right below it (same occupation, department, activities) — then divide the headcount.
  async function splitActivity(r) {
    if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then try again.' })
    const { error } = await supabase.from('ote_activities').insert({ job_id: id, sort_order: r.sort_order, source: 'auditor', occupation: r.occupation,
      department: r.department, no_affected: null, work_activities: r.work_activities, updated_by: whoName() })
    if (error) return failed("Couldn't split the row", error.message)
    toast('Row copied below', 'success', { detail: 'Now change each row so they are different, and split the number of employees.' })
    await load()
  }
  // Remove: kept on record with the reason (shown in the report and the client email); Restore puts it back.
  function removeActivity(r, why) {
    setAct(r.id, { removed_at: new Date().toISOString(), removed_reason: why, removed_note: null, removed_by: whoName() })
    setRemoving(null)
  }
  function restoreActivity(r) { setAct(r.id, { removed_at: null, removed_reason: null, removed_note: null, removed_by: null }) }

  // ── Step 3: items for the client ──
  function outstandingEmail() {
    const pic = otePic(job)
    const lines = openIssues.map((i, n) => `${n + 1}. ${i.finding}${i.client_action ? `\n   Please send: ${i.client_action}` : ''}`).join('\n')
    const body = `Dear ${pic.name || 'Sir/Madam'},\n\nThank you for your time during our OTE verification visit on ${fmtDay(job.audit_date)}. To complete the endorsement of your Risk Register, please send us the following within 30 days:\n\n${lines}\n\nPlease reply to this email with the documents.\n\nThank you.`
    window.open(`mailto:${pic.email}?cc=query@ccis.com.sg&subject=${encodeURIComponent(`OTE verification - items to send -${client.name || ''}`)}&body=${encodeURIComponent(body)}`)
  }

  // ── Step 4: sign ──
  const prelim = (() => {
    const reasons = []
    OTE_ITEMS.forEach(it => { const r = itemResult(it, answers, rows); if (r !== S) reasons.push(`${it.code} ${it.title}${r ? ' is not satisfactory' : ' is not answered'}`) })
    // Activities: one short line per kind of problem (not one per row).
    const n = arr => arr.map(i => i + 1).join(', ')
    const unanswered = rows.map((r, i) => (!r.risk_level || r.annex_a == null) ? i : -1).filter(i => i >= 0)
    const notLow = rows.map((r, i) => r.risk_level === 'Not low' ? i : -1).filter(i => i >= 0)
    const annex = rows.map((r, i) => r.annex_a === true ? i : -1).filter(i => i >= 0)
    if (unanswered.length) reasons.push(unanswered.length === rows.length ? `No activity has its risk level and Annex A answer yet (step 2)` : `Activit${unanswered.length > 1 ? 'ies' : 'y'} ${n(unanswered)}: risk level or Annex A not answered (step 2)`)
    if (notLow.length) reasons.push(`Activit${notLow.length > 1 ? 'ies' : 'y'} ${n(notLow)}: not LOW risk`)
    if (annex.length) reasons.push(`Activit${annex.length > 1 ? 'ies' : 'y'} ${n(annex)}: in MOM Annex A — remove ${annex.length > 1 ? 'them' : 'it'} (step 2)`)
    if (openIssues.length) reasons.push(`${openIssues.length} item${openIssues.length > 1 ? 's' : ''} still open for the client`)
    if (!acceptedRa) reasons.push('No RA uploaded yet (step 2)')
    if (!String(job.audit_site_address || '').trim()) reasons.push('Site address missing — ask the office to fill it in on the job page')
    return reasons
  })()
  async function sign(notEndorsed) {
    if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then try again.' })
    let notEndorsedReason = null
    if (notEndorsed) {
      notEndorsedReason = window.prompt('Sign as NOT endorsed — why? (it goes into the report)', '')
      if (notEndorsedReason === null) return
      if (!notEndorsedReason.trim()) return toast('Give the reason', 'warning')
      if (!window.confirm(`Sign ${client.name} as NOT endorsed?\n\nThis cannot be undone. MOM's endorsement will NOT be issued.`)) return
    } else if (!window.confirm(`Sign the OTE report and MOM's endorsement for ${client.name}?\n\nYour signature will be placed on MOM's form. After signing, nothing can be changed.`)) return
    setBusy('sign'); setMissing([])
    try {
      const r = await oteAction({ action: 'sign', jobId: id, notEndorsed: !!notEndorsed, notEndorsedReason: notEndorsedReason ? notEndorsedReason.trim() : null, raReason: raReason.trim() || null })
      toast(r.conclusion === 'SATISFACTORY' ? 'Signed — the endorsement is being prepared' : 'Signed as not endorsed', 'success')
    } catch (e) {
      const msg = String(e.message || e)
      const list = msg.split('\n• ').slice(1)
      if (list.length) setMissing(list); else failed("Can't sign yet", msg)
    }
    setBusy('')
    await load()
  }

  // ── Gates for "Next →" ──
  const step1Missing = !started ? 'Press “Start the job” first' : !ws.start_at ? 'Enter the start time first' : !parts.some(p => String(p.name || '').trim()) ? 'Add at least one person at the opening meeting' : null
  const step2Missing = !ras.length ? 'Upload the client’s RA / Risk Register first'
    : !rows.length ? 'At least one work activity is needed'
    : incompleteRows.length ? `Not checked yet: activit${incompleteRows.length > 1 ? 'ies' : 'y'} ${incompleteRows.map(i => i + 1).join(', ')} (RA reference, risk level and Annex A)` : null
  const step3Missing = OTE_ITEMS.some(it => it.kind !== 'computed' && itemResult(it, answers, rows) == null) ? 'Answer every checklist item first' : null
  const STEPS = [[1, 'Start'], [2, 'Activities & RA'], [3, 'Checklist'], [4, 'Finish']]
  const gate = { 1: step1Missing, 2: step2Missing, 3: step3Missing }
  const nextBtn = (n) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 14 }}>
      {gate[n] && <span style={{ fontSize: 13, color: 'var(--text3)' }}>{gate[n]}</span>}
      <button className="primary" disabled={!!gate[n]} onClick={() => { setStep(n + 1); window.scrollTo(0, 0) }}>Next →</button>
    </div>
  )
  const latestReport = k => reports.find(r => r.kind === k && r.status !== 'superseded')

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="sm" onClick={() => navigate('/my-audits')}>← My Audits</button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: saveState === 'saved' ? '#27500a' : '#854F0B' }}>
          {saveState === 'saved' ? '✓ Saved' : saveState === 'saving' ? 'Saving…' : `● ${pending} change${pending === 1 ? '' : 's'} not saved yet`}
        </span>
        {pending > 0 && <button className="sm" onClick={saveNow}>Save now</button>}
      </div>
      <h1 style={{ marginBottom: 2 }}>{client.name || '—'}</h1>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>OTE verification · {fmtDay(job.audit_date) || 'date to be set'} · {job.audit_site_address || 'site to be set'}</div>

      {!assigned && <div className="notice notice-info" style={{ marginBottom: 12 }}>You are viewing this worksheet. Only the assigned auditor ({job.auditor_name || '—'}) can fill it in.</div>}
      {assigned && hasSig === false && <div className="notice" style={{ marginBottom: 12, background: '#FFF3E0', color: '#854F0B' }}>⚠ Your signature is not in AuditPM yet — ask the office to add it before you sign.</div>}

      {/* Step bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {STEPS.map(([n, label]) => (
          <button key={n} className={step === n ? 'primary sm' : 'sm'} onClick={() => setStep(n)}
            disabled={editable && n > 1 && [1, 2, 3].slice(0, n - 1).some(k => gate[k]) && step < n}>{n} {label}</button>
        ))}
      </div>

      {/* ── STEP 1 ── */}
      {step === 1 && (
        <div className="card">
          <h2 className="card-title">1 · Start</h2>
          {(stage === 'scheduled' || stage === 'confirmed') && assigned && job.status !== 'Cancelled' && (
            <div style={{ marginBottom: 14 }}>
              <button className="primary" disabled={busy === 'start' || !job.audit_date} onClick={async () => {
                if (await act('start', { action: 'start' }, 'Job started')) { if (!ws.start_at) setW('start_at', new Date().toISOString()) }
              }}>Start the job</button>
              <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 10 }}>Press this when you arrive on site — the start time is filled in for you.</span>
            </div>
          )}
          {!job.audit_date && <div style={{ fontSize: 13, color: '#854F0B', marginBottom: 8 }}>The job date is not set yet — please ask the office to set it on the job page.</div>}
          <div style={{ fontSize: 13, marginBottom: 10 }}>Site: <strong>{job.audit_site_address || <span style={{ color: '#854F0B' }}>not filled in yet — ask the office (needed before you can sign)</span>}</strong></div>
          <div className="grid-2">
            <div className="field"><label>Start time *</label>
              <input type="time" disabled={!editable || !job.audit_date} value={sgTime(ws.start_at)} onChange={e => setW('start_at', atTime(job.audit_date, e.target.value))} /></div>
            <div className="field"><label>End time</label>
              <input type="time" disabled={!editable || !job.audit_date} value={sgTime(ws.end_at)} onChange={e => setW('end_at', atTime(job.audit_date, e.target.value))} /></div>
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, margin: '6px 0' }}>Opening meeting — attendance *</div>
          {parts.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <input placeholder="Name" disabled={!editable} value={p.name || ''} onChange={e => setParts(l => l.map(x => x.id === p.id ? { ...x, name: e.target.value } : x))} onBlur={e => updAttendee(p.id, { name: e.target.value })} style={{ flex: '1 1 180px' }} />
              <input placeholder="Designation" disabled={!editable} value={p.designation || ''} onChange={e => setParts(l => l.map(x => x.id === p.id ? { ...x, designation: e.target.value } : x))} onBlur={e => updAttendee(p.id, { designation: e.target.value })} style={{ flex: '1 1 160px' }} />
              {editable && <button className="sm danger" onClick={() => delAttendee(p.id)}>×</button>}
            </div>
          ))}
          {editable && <button className="sm" onClick={addAttendee}>+ Add attendee</button>}

          <div style={{ fontWeight: 600, fontSize: 13, margin: '16px 0 6px' }}>Session photo</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {photos.map(p => (
              <span key={p.id} style={{ fontSize: 13, background: 'var(--bg2)', padding: '5px 10px', borderRadius: 6 }}>
                <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => openFile(p)}>📷 {p.file_name}</span>
                {editable && <span style={{ marginLeft: 8, cursor: 'pointer', color: 'var(--danger)' }} onClick={() => removePhoto(p.id)}>×</span>}
              </span>
            ))}
            {editable && <TakePhotoButton disabled={!!busy} onPhoto={uploadPhoto} />}
            {editable && <label className="sm" style={{ cursor: 'pointer', padding: '8px 14px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 13 }}>
              Upload session photo<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; uploadPhoto(f) }} /></label>}
            {busy === 'photo' && <span style={{ fontSize: 13, color: 'var(--text3)' }}>Uploading…</span>}
            {!editable && photos.length === 0 && <span style={{ fontSize: 13, color: 'var(--text3)' }}>No photo yet{assigned && !started ? ' — press “Start the job” first' : ''}.</span>}
          </div>
          {photos.length > 0 ? (
            // Social-media consent, as Internal Audit / bizSAFE (owner, 5 Nov 2026): the photo is always kept as session
            // evidence; Yes / No only says whether CCIS may post it. (An older tick saved as true counts as "Yes".)
            <div style={{ marginTop: 10, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
              <span style={!d.photo_consent ? { color: 'var(--danger)' } : undefined}>Did everyone agree to have the photo posted on CCIS’ Social Media?</span>
              {['Yes', 'No'].map(v => (
                <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: editable ? 'pointer' : 'default', margin: 0 }}>
                  <input type="radio" name="ote-photo-consent" disabled={!editable} checked={d.photo_consent === v || (v === 'Yes' && d.photo_consent === true)}
                    onChange={() => setW('data.photo_consent', v)} style={{ width: 'auto', margin: 0 }} /> {v}
                </label>))}
            </div>
          ) : (
            // Same words and layout as Internal Audit / bizSAFE (owner, 5 Nov 2026).
            <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text2)', flexWrap: 'wrap' }}>
              <span>Provide reason if no session photo is available:</span>
              <input disabled={!editable} value={d.no_photo_reason || ''} onChange={e => setW('data.no_photo_reason', e.target.value)} placeholder="Reason"
                style={{ fontSize: 13, maxWidth: 240, ...(!String(d.no_photo_reason || '').trim() ? { borderColor: 'var(--danger)' } : {}) }} />
            </div>
          )}
          {nextBtn(1)}
        </div>
      )}

      {/* ── STEP 2 ── */}
      {step === 2 && (
        <>
          <div className="card" style={{ marginBottom: 14 }}>
            <h2 className="card-title">2 · Documents</h2>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Risk Assessment / Risk Register *</div>
            {ras.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>Not uploaded yet.</div>}
            {ras.map(r => (
              <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '4px 0', flexWrap: 'wrap' }}>
                <span>Version {r.version_no}</span>
                {r.file && <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => openFile(r.file)}>{r.file.file_name}</span>}
                {r.accepted ? <span className="badge badge-ok">✓ Accepted</span>
                  : editable && <button className="sm" disabled={!!busy} onClick={() => act('accept', { action: 'accept-document', documentId: r.id }, 'Version accepted')}>Use this version instead</button>}
                {/* Effective date printed on this RA (goes on MOM's form) — a normal date picker, saved with the button. */}
                {r.accepted && (editable ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: r.effective_date ? 'var(--text2)' : '#854F0B' }}>Effective date on the RA{r.effective_date ? '' : ' *'}:</span>
                    <input type="date" max={sgToday()} value={raDate[r.id] ?? r.effective_date ?? ''} onChange={e => setRaDate(x => ({ ...x, [r.id]: e.target.value }))} style={{ width: 'auto' }} />
                    {(raDate[r.id] && raDate[r.id] !== r.effective_date) && <button className="sm primary" disabled={!!busy}
                      onClick={async () => { if (await act('radate', { action: 'accept-document', documentId: r.id, effectiveDate: raDate[r.id] }, 'Effective date saved')) setRaDate(x => ({ ...x, [r.id]: undefined })) }}>Save date</button>}
                  </span>) : r.effective_date && <span style={{ color: 'var(--text2)' }}>effective {fmtDay(r.effective_date)}</span>)}
              </div>
            ))}
            {latestRa && acceptedRa && latestRa.id !== acceptedRa.id && <div style={{ fontSize: 13, color: '#854F0B' }}>The accepted RA is not the latest version.</div>}
            {Object.values(answers).some(a => a.recheck) && <div style={{ fontSize: 13, color: '#854F0B', fontWeight: 600, marginTop: 4 }}>RA changed — please re-confirm A1 to B5 in step 3.</div>}
            {editable && <label className="sm" style={{ display: 'inline-block', cursor: 'pointer', padding: '7px 12px', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 8, fontSize: 13, marginTop: 6 }}>
              {ras.length ? 'Upload revised RA' : 'Upload RA / Risk Register'}<input type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; uploadDoc(f, 'ra') }} /></label>}

            <div style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 4px' }}>Safe work procedures (SWPs)</div>
            {swps.map(r => <div key={r.id} style={{ fontSize: 13 }}><span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => r.file && openFile(r.file)}>{r.file?.file_name || 'SWP'}</span></div>)}
            {editable && <label className="sm" style={{ display: 'inline-block', cursor: 'pointer', padding: '7px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 13, marginTop: 4 }}>
              Upload SWP<input type="file" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; uploadDoc(f, 'swp') }} /></label>}

            <div style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 4px' }}>Other evidence</div>
            {others.map(r => <div key={r.id} style={{ fontSize: 13 }}>{r.label || 'Document'} · <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => r.file && openFile(r.file)}>{r.file?.file_name}</span></div>)}
            {editable && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                <input value={otherLabel} onChange={e => setOtherLabel(e.target.value)} placeholder="What is it? e.g. Shift roster" style={{ maxWidth: 260 }} />
                <label className="sm" style={{ display: 'inline-block', cursor: otherLabel.trim() ? 'pointer' : 'not-allowed', opacity: otherLabel.trim() ? 1 : 0.5, padding: '7px 12px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 13 }}>
                  Upload other file<input type="file" disabled={!otherLabel.trim()} style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; uploadDoc(f, 'other') }} /></label>
                {!otherLabel.trim() && <span style={{ fontSize: 13, color: 'var(--text3)' }}>Type what it is first</span>}
              </div>)}
            {busy === 'doc' && <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 8 }}>Uploading…</span>}
          </div>

          <div className="card">
            <h2 className="card-title">Work activities ({rows.length})</h2>
            {/* Big reminder: these rows were filled in BEFORE the visit — go through each one with the client. */}
            {rows.length === 0 && !removed.length ? (
              <div style={{ background: '#FFF3E0', border: '2px solid #E6A817', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: '#6b4e00', lineHeight: 1.6 }}>
                <strong>⚠ No activities yet</strong>{job.form_data?.activities_later ? ' — the client said they would give you the list.' : '.'} Go through the overtime work with the client and press <strong>+ Add activity</strong> for each one.
              </div>
            ) : (
            <div style={{ background: '#FFF3E0', border: '2px solid #E6A817', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: '#6b4e00', lineHeight: 1.6 }}>
              <strong>⚠ These activities were filled in BEFORE the visit</strong> (from the client’s questionnaire).
              Go through <strong>each one with the client</strong> on site — change, add or remove rows so they match the actual overtime work.
              Your version goes on MOM’s form; what the client wrote is kept on record.
            </div>)}
            {rows.map((r, i) => {
              const orig = r.client_original || null
              const changed = orig && ['occupation', 'department', 'work_activities'].some(k => String(orig[k] || '') !== String(r[k] || '')) || (orig && String(orig.no_affected || '') !== String(r.no_affected || ''))
              const todo = isIncomplete(r)
              return (
                <div key={r.id} style={{ border: todo ? '2px solid #E6A817' : '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13 }}>Activity {i + 1} of {rows.length}{r.source === 'auditor' ? ' · added by auditor' : ''}
                      {todo ? <span className="badge badge-warn" style={{ marginLeft: 8 }}>Not checked yet</span> : <span className="badge badge-ok" style={{ marginLeft: 8 }}>✓ Checked</span>}</strong>
                    {editable && removing !== r.id && <span style={{ display: 'flex', gap: 6 }}>
                      <button className="sm" onClick={() => splitActivity(r)}>Split this row</button>
                      <button className="sm" onClick={() => setRemoving(r.id)}>Remove…</button>
                    </span>}
                  </div>
                  {editable && removing === r.id && (
                    <div style={{ background: '#FDECEA', borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 13 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Remove “{String(r.work_activities || 'this activity').split('\n')[0]}”? Why?</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="sm danger" onClick={() => removeActivity(r, REMOVE_REASONS[0])}>It is on MOM’s banned list (Annex A)</button>
                        <button className="sm danger" onClick={() => removeActivity(r, REMOVE_REASONS[1])}>The client withdrew it</button>
                        <button className="sm" onClick={() => setRemoving(null)}>Keep it</button>
                      </div>
                      <div style={{ color: 'var(--text3)', marginTop: 6 }}>It stays on record and in the report. You can restore it below.</div>
                    </div>)}
                  <div className="field"><label>Work activities *</label>
                    <textarea rows={3} disabled={!editable} value={r.work_activities || ''} onChange={e => setAct(r.id, { work_activities: e.target.value })} style={box} /></div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                    <div className="field"><label>Occupation</label><input disabled={!editable} value={r.occupation || ''} onChange={e => setAct(r.id, { occupation: e.target.value })} /></div>
                    <div className="field"><label>Department</label><input disabled={!editable} value={r.department || ''} onChange={e => setAct(r.id, { department: e.target.value })} /></div>
                    <div className="field"><label>No. affected</label><input type="number" min="0" disabled={!editable} value={r.no_affected || ''} onWheel={e => e.currentTarget.blur()} onChange={e => setAct(r.id, { no_affected: e.target.value })} /></div>
                    <div className="field"><label>RA reference *</label><input disabled={!editable} value={r.ra_reference || ''} onChange={e => setAct(r.id, { ra_reference: e.target.value })} placeholder="e.g. RA-PRD-03" /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 4 }}>
                    <div><div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>Risk level after controls *</div>
                      <Choice disabled={!editable} value={r.risk_level} onChange={v => setAct(r.id, { risk_level: v })} options={[['Low', 'Low'], ['Not low', 'Not low', 'bad']]} /></div>
                    <div><div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>On MOM’s banned list (Annex A)? *</div>
                      <Choice disabled={!editable} value={r.annex_a} onChange={v => setAct(r.id, { annex_a: v })} options={[[false, 'No'], [true, 'Yes', 'bad']]} /></div>
                  </div>
                  {changed && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>Client wrote: {[orig.occupation, orig.department, orig.no_affected ? `${orig.no_affected} affected` : ''].filter(Boolean).join(' · ')} — {orig.work_activities}</div>}
                </div>
              )
            })}
            {editable && <button className="sm" onClick={addActivity}>+ Add activity</button>}
            {removed.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text2)' }}>
                <div style={{ fontWeight: 600 }}>Removed ({removed.length}) — kept on record</div>
                {removed.map(r => <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', flexWrap: 'wrap' }}>
                  <span>• {String(r.work_activities || '—').split('\n')[0]} — {r.removed_reason === 'MOM Annex A' ? 'on MOM’s banned list (Annex A)' : 'client withdrew'}</span>
                  {editable && <button className="sm" onClick={() => restoreActivity(r)}>Restore</button>}
                </div>)}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <button className="sm" onClick={() => setShowAnnex(s => !s)}>{showAnnex ? 'Hide' : 'Show'} MOM Annex A</button>
              {showAnnex && <ol style={{ fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>
                {OTE_ANNEX_A.map((c, i) => <li key={i}>{c.title}{c.items?.length ? ` ${c.items.join('; ')}` : ''}{c.groups ? ` ${c.groups.map(g => `${g.head}: ${g.items.join('; ')}`).join(' · ')}` : ''}</li>)}
              </ol>}
            </div>
            {nextBtn(2)}
          </div>
        </>
      )}

      {/* ── STEP 3 ── */}
      {step === 3 && (
        <div className="card">
          <h2 className="card-title">3 · Checklist</h2>
          {OTE_SECTIONS.map(sec => (
            <div key={sec.code} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{sec.code}. {sec.title}</div>
              {OTE_ITEMS.filter(it => it.section === sec.code).map(it => {
                const a = answers[it.code] || {}
                const res = itemResult(it, answers, rows)
                const issue = openIssues.find(i => i.item_code === it.code)
                return (
                  <div key={it.code} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 8, background: a.recheck ? '#FFF8E1' : 'var(--bg)' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{it.code} {it.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', margin: '2px 0 8px' }}>{it.verify}</div>
                    {a.recheck && <div style={{ fontSize: 13, color: '#854F0B', fontWeight: 600, marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      The RA was revised — check this item against the new version.
                      {editable && <button className="sm" onClick={() => setAns(it.code, {})}>Still correct — re-confirm</button>}</div>}
                    {it.kind === 'computed' && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: res === S ? '#27500a' : '#791F1F' }}>
                        {it.code === 'A2' ? (res === S ? '✓ Every activity has an RA reference' : `✗ ${rows.filter(r => !String(r.ra_reference || '').trim()).length} activit${rows.filter(r => !String(r.ra_reference || '').trim()).length === 1 ? 'y has' : 'ies have'} no RA reference → step 2`)
                          : (res === S ? '✓ No activity is in MOM Annex A' : '✗ An activity is in Annex A or not answered → step 2')}
                      </div>
                    )}
                    {it.kind === 'result' && (
                      <>
                        <Choice disabled={!editable} value={a.result} onChange={v => setAns(it.code, { result: v })} options={[[S, 'Satisfactory'], [NS, 'Not Satisfactory', 'bad']]} />
                        {it.needsLow && a.result === S && res !== S && <div style={{ fontSize: 13, color: '#791F1F', marginTop: 6 }}>✗ Not every activity is LOW risk (step 2), so this item is not satisfactory.</div>}
                      </>
                    )}
                    {(it.kind === 'parts' || it.kind === 'factors') && it.parts.map(p => (
                      <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '4px 0', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13 }}>{p.label}</span>
                        <Choice disabled={!editable} value={(a.factors || {})[p.key]} onChange={v => setAns(it.code, { factors: { ...(a.factors || {}), [p.key]: v } })}
                          options={it.kind === 'factors' ? [['Yes', 'Yes'], ['No', 'No', 'bad'], ['N/A', 'N/A', 'na']]
                            : [[S, 'Satisfactory'], [NS, 'Not Satisfactory', 'bad'], ...(p.na ? [['N/A', 'N/A', 'na']] : [])]} />
                      </div>
                    ))}
                    {it.kind !== 'computed' && (
                      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
                        <label>Finding / remarks{res === NS ? ' *' : ''}</label>
                        <textarea rows={2} disabled={!editable} value={a.finding || ''} onChange={e => setAns(it.code, { finding: e.target.value })} style={box} />
                      </div>
                    )}
                    {it.swp && swps.length === 0 && (
                      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}><label>No SWP uploaded — give the reason *</label>
                        <input disabled={!editable} value={d.swp_reason || ''} onChange={e => setW('data.swp_reason', e.target.value)} /></div>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>Evidence: {it.evidence}</div>

                    {/* Not satisfactory → still needed from the client */}
                    {it.kind !== 'computed' && res === NS && editable && !issue && (
                      <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#FDECEA' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#791F1F', marginBottom: 6 }}>Needs something from the client?</div>
                        {/* Kept on this device / saved with the worksheet, so a dropped connection doesn't lose it. */}
                        <input placeholder="What must the client send? (e.g. Revised RA with LOW final risk)" value={d['ca_' + it.code] || ''} onChange={e => setW('data.ca_' + it.code, e.target.value)} style={{ ...box, marginBottom: 6 }} />
                        <button className="sm" disabled={!!busy || !String(a.finding || '').trim()} title={!String(a.finding || '').trim() ? 'Write the finding first' : ''}
                          onClick={async () => { if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then try again.' }); act('issue', { action: 'open-issue', itemCode: it.code, finding: a.finding.trim(), clientAction: d['ca_' + it.code] || '' }, 'Added to the list for the client') }}>
                          Add to the list for the client</button>
                      </div>
                    )}
                    {issue && (
                      <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#FFF3E0', fontSize: 13 }}>
                        <div><strong>Still needed from the client:</strong> {issue.client_action || issue.finding}</div>
                        {editable && res === S && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                            <input placeholder="What did the client send? (e.g. Revised RA rev 4, SWP-07)" value={closeText[it.code] || ''} onChange={e => setCloseText(x => ({ ...x, [it.code]: e.target.value }))} style={{ flex: '1 1 260px' }} />
                            <button className="sm primary" disabled={!!busy || !String(closeText[it.code] || '').trim()} onClick={async () => {
                              if (!(await saveNow())) return toast('Changes not saved yet', 'warning', { detail: 'Check your Wi-Fi, then try again.' })
                              if (await act('close', { action: 'close-issue', issueId: issue.id, comment: closeText[it.code].trim() }, 'Item closed')) setCloseText(x => ({ ...x, [it.code]: '' }))
                            }}>Close this item</button>
                          </div>
                        )}
                        {editable && res !== S && <div style={{ color: 'var(--text3)', marginTop: 4 }}>When the client sends it, upload it (step 2), re-answer this item as Satisfactory, then close the item.</div>}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}

          {openIssues.length > 0 && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg2)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{openIssues.length} item{openIssues.length > 1 ? 's' : ''} still needed from the client</div>
              <ol style={{ fontSize: 13, margin: '0 0 8px', paddingLeft: 20 }}>{openIssues.map(i => <li key={i.id}>{i.client_action || i.finding}</li>)}</ol>
              {stage === 'awaiting_closeout' && <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>List emailed — AuditPM reminds the client at day 7, 14 and 21. Added another item since? Email the updated list again.</div>}
              {editable && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="sm" onClick={outstandingEmail}>1. Email the list to the client (Outlook)</button>
                    <button className="sm primary" disabled={!!busy} onClick={() => act('emailed', { action: 'list-emailed', to: otePic(job).email }, 'Recorded — the client has 30 days')}>2. I’ve emailed the list</button>
                  </div>}
            </div>
          )}
          {nextBtn(3)}
        </div>
      )}

      {/* ── STEP 4 ── */}
      {step === 4 && (
        <div className="card">
          <h2 className="card-title">4 · Finish</h2>
          {OPEN.includes(stage) ? (
            <>
              <div className="field"><label>Effective date of the Risk Assessment report * <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(the date the accepted RA version takes effect — goes on MOM’s form)</span></label>
                <input type="date" disabled={!editable} max={sgToday()} value={ws.effective_date || ''} onChange={e => setW('effective_date', e.target.value || null)} style={{ maxWidth: 200 }} />
                {!ws.effective_date && acceptedRa?.effective_date && acceptedRa.effective_date <= sgToday() && editable && <button className="sm" style={{ marginLeft: 8 }} onClick={() => setW('effective_date', acceptedRa.effective_date)}>Use {fmtDay(acceptedRa.effective_date)} (from the accepted RA)</button>}
              </div>
              <div className="field"><label>Observations (optional)</label>
                <textarea rows={3} disabled={!editable} value={ws.observations || ''} onChange={e => setW('observations', e.target.value)} style={box} /></div>
              {latestRa && acceptedRa && latestRa.id !== acceptedRa.id && (
                <div className="field"><label>The accepted RA is not the latest version — reason *</label>
                  <input disabled={!editable} value={raReason} onChange={e => setRaReason(e.target.value)} /></div>
              )}
              <div style={{ padding: 12, borderRadius: 8, background: prelim.length ? '#FDECEA' : '#eaf3de', fontSize: 13, marginBottom: 12 }}>
                {prelim.length
                  ? <><strong style={{ color: '#791F1F' }}>Not satisfactory yet:</strong><ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{prelim.map((r, i) => <li key={i}>{r}</li>)}</ul></>
                  : <strong style={{ color: '#27500a' }}>✓ Everything is satisfactory — ready to sign.</strong>}
              </div>
              {missing.length > 0 && (
                <div style={{ padding: 12, borderRadius: 8, background: '#FFF3E0', fontSize: 13, marginBottom: 12 }}>
                  <strong style={{ color: '#854F0B' }}>Can’t sign yet — still needed:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
              {editable && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {!prelim.length && <button className="primary" disabled={busy === 'sign'} onClick={() => sign(false)}>{busy === 'sign' ? 'Signing…' : 'Sign & finish'}</button>}
                  {/* Never while the client is still sending items — that is a wait, not a "not endorsed". */}
                  {!openIssues.length && stage !== 'awaiting_closeout' && prelim.length > 0 && <button className="danger" disabled={busy === 'sign'} onClick={() => sign(true)}>Sign as Not endorsed</button>}
                  {openIssues.length > 0 && <span style={{ fontSize: 13, color: '#854F0B' }}>Waiting for the client’s items (step 3). Close them first, then sign.</span>}
                  {!prelim.length && <span style={{ fontSize: 13, color: 'var(--text3)' }}>{job.invoice_clearance_checked_at ? 'Payment is recorded — you can send the endorsement right after signing.' : 'Not paid yet — after signing, Accounts is told and the office sends it once paid.'}</span>}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, marginBottom: 10 }}>
                {stage === 'signing' && 'Signing… this page updates by itself.'}
                {stage === 'ready_to_send' && <strong>Signed. Send the endorsement to the client:</strong>}
                {stage === 'awaiting_payment' && 'Signed. Not paid yet — the office will send the endorsement once payment is recorded. You’re done.'}
                {stage === 'sending' && 'Sending…'}
                {stage === 'completed' && '✓ Endorsement sent to the client. Done.'}
                {stage === 'not_endorsed' && 'Signed as not endorsed. The office sends the notice to the client. You’re done.'}
                {stage === 'closed' && 'Closed — not endorsed.'}
                {stage === 'void' && 'Voided.'}
              </div>
              {['endorsement', 'report'].map(k => { const r = latestReport(k); return r ? (
                <div key={k} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                  {k === 'report' ? 'CCIS report' : 'MOM endorsement (Excel)'} v{r.version}: {r.status === 'issued' ? 'ready' : r.status === 'error' ? 'could not be prepared — tell the office' : 'being prepared… (this page updates by itself)'}
                  {r.status === 'issued' && <button className="sm" onClick={async () => { try { const x = await oteAction({ action: 'report-url', jobId: id, reportId: r.id }); window.open(x.url, '_blank', 'noopener') } catch (e) { failed("Couldn't open it", e.message) } }}>{k === 'endorsement' ? 'View MOM form' : 'View report'}</button>}
                </div>) : null })}
              {stage === 'ready_to_send' && assigned && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="email" value={sendTo} onChange={e => setSendTo(e.target.value)} style={{ maxWidth: 280 }} />
                  <button className="primary" disabled={!!busy || !sendTo.trim() || latestReport('endorsement')?.status !== 'issued'} onClick={() => {
                    if (!window.confirm(`Send the endorsement to ${sendTo.trim()}? (query@ in CC, so the office knows)`)) return
                    act('send', { action: 'send', to: sendTo.trim() }, 'Endorsement sent')
                  }}>{busy === 'send' ? 'Sending…' : 'Send endorsement'}</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
